import { DOCUMENT, DatePipe, TitleCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit, OnDestroy, signal, ViewChild } from '@angular/core';
import { Icon } from '../../common/components/general/icon/icon';
import { StatusMessageService } from './status-message.service';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { ReusableService } from '../../common/services/reusable-services';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { SRP_ROUTES } from '../../common/constants/routes';
import { Messages, MessageResponse } from './status-message.model';
import { environment } from '../../../environments/environment';
import { Button } from '../../common/components/general/button/button';
import { ChildMenu } from '../header-model';
import { COMPONENT_META_DATA } from '../../common/constants/component-meta-data';
import { Label } from '../../common/models/page-components';
import { ContentFilterPipe } from '../../common/pipes/content-filter-pipe';
import { HeaderService } from '../header.service';
import { firstValueFrom } from 'rxjs';
import { SrpSnackbarService } from '../../common/services/srp-snackbar.service';
import { convertToLocalTimezone } from '../../common/utils/utils';
import { LogoutService } from '../../common/services/logout-service';

@Component({
  selector: 'srp-status-message',
  imports: [Icon, DatePipe, MatIconModule, MatBadgeModule, MatMenuModule, Button, TitleCasePipe, ContentFilterPipe],
  templateUrl: './status-message.html',
  styleUrl: './status-message.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentFilterPipe, DatePipe, TitleCasePipe]
})
export class StatusMessage implements OnInit, AfterViewInit, OnDestroy {

  toastCount = signal<number>(0);
  duration: number = 25; // Displays the toast message for ${duration} seconds before it disappears
  interval: number = 60; // Polls the API every ${interval} seconds and gets new toast messages
  toasts: Messages[] = [];
  showTopToasts = signal<Messages[]>([]);
  isToastShown = signal<boolean>(false);
  maxToastPopups = 5; // Maximum number has been confirmed
  panelToasts = signal<Messages[]>([]);
  panelToastsCount = 10;
  private readonly overlayBoundingBoxClass = 'status-messages-overlay-bounding-box--srp-unique';
  private readonly overlayAlignedClass = 'status-messages-overlay-aligned';
  // Matches/exceeds the MatMenu (MDC) close animation duration so we don't strip
  // the custom alignment styles while the panel is still visually closing.
  private readonly overlayResetDelay = 250;
  private resetAlignmentTimeout: ReturnType<typeof setTimeout> | null = null;
  statusMessagePollingInterval: ReturnType<typeof setInterval> | null = null;
  showMessageTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly statusMessageService: StatusMessageService = inject(StatusMessageService);
  private readonly reusableService: ReusableService = inject(ReusableService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(MatMenuTrigger, {static: false}) matMenuTrigger: MatMenuTrigger;
  @Input() appContext: string;
  contentFilterPipe = inject(ContentFilterPipe);
  datePipe: DatePipe = inject(DatePipe);
  headerService = inject(HeaderService);
  statusMessagesLabels: Label[] = [];
  LABELS = COMPONENT_META_DATA.HEADER.LABELS;
  snackbarService = inject(SrpSnackbarService);
  logoutService = inject(LogoutService);
  serviceMessagesRoute = `/${SRP_ROUTES.STATUS_MESSAGES.PATH}`;

  get stackedTopToastsCount(): number {
    return Math.max(this.showTopToasts().length - this.maxToastPopups, 0);
  }

  get visibleTopToasts(): Messages[] {
    return this.showTopToasts().slice(0, this.maxToastPopups);
  }

  get stackedAnchorToasts(): Messages[] {
    return this.showTopToasts().slice(this.maxToastPopups, this.maxToastPopups + 3);
  }

  isAlertToast(toast: Messages | undefined): boolean {
    return (toast?.type || '').trim().toLowerCase() === 'alert';
  }

  ngOnInit(): void {
    this.initializeLabels();
    if(environment.ENABLE_STATUS_MESSAGES) {
      this.fetchInitialStatusMessages();
      this.startPollingForStatusMessages();
      this.setToastCount();
    }
  }

  setToastCount(): void {
    const toastCount$ = this.statusMessageService?.toastCount$;
    if (!toastCount$) {
      return;
    }
    toastCount$.subscribe((count) => {
      this.toastCount.set(count);
    });
  }
  
  async startPollingForStatusMessages(): Promise<void> {
    this.statusMessagePollingInterval = setInterval(async () => {
      if(this.logoutService.isLoggedIn()) {
        try {
          const toasts = await firstValueFrom(this.statusMessageService.getToastMessagePollingResponse({isNotified: false, isRead: false}, null, true));
          this.toasts = this.formatMessageResponse(toasts?.data?.messages);
          this.showTopToasts.set(this.toasts);
          this.statusMessageService.setToastCount(toasts?.data?.unreadMessagesCount);
          const payload = this.toasts.map(toast => toast.id); // payload length is non-zero only when unread messages exist
          if(payload.length > 0) {
            this.showStatusMessages();
            try {
              await firstValueFrom(this.statusMessageService.updateStatusMessageStatus(payload, false, true));
            }
            catch(e) {
              this.snackbarService.show({
                message: this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.UPDATE_FAILED_MSG) || 'Unable to update service messages. Please try again later.',
                type: 'warning',
              });
            }
          }
        }
        catch(e) {
          this.snackbarService.show({
            message: this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.LIST_FAILED_MSG) || 'Unable to retrieve service messages. Please try again later.',
            type: 'warning',
          });
        }
      }
      else {
        this.clearAllTimers();
      }
    }, this.interval * 1000);
  }

  clearAllTimers(): void {
    clearTimeout(this.resetAlignmentTimeout);
    this.resetStatusMessagesPanelAlignment();
    clearInterval(this.statusMessagePollingInterval);
    clearTimeout(this.showMessageTimeout);
  }

  async fetchInitialStatusMessages(): Promise<void> {
    try {
      const response = await firstValueFrom(this.statusMessageService.getToastMessagePollingResponse({isNotified: false, isRead: false}, null, true));
      this.toasts = this.formatMessageResponse(response?.data?.messages);
      this.statusMessageService.setToastCount(response?.data?.unreadMessagesCount);
      this.showTopToasts.set(this.toasts);
      const payload = this.toasts.map(toast => toast.id);
      if(payload.length > 0) {
        this.showStatusMessages();
        try {
          await firstValueFrom(this.statusMessageService.updateStatusMessageStatus(payload, false, true));
        } catch(e) {
          this.snackbarService.show({
            message: this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.UPDATE_FAILED_MSG) || 'Unable to update service messages. Please try again later.',
            type: 'warning',
          });
        }
      }
    } catch(e) {
      this.snackbarService.show({
        message: this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.LIST_FAILED_MSG) || 'Unable to retrieve service messages. Please try again later.',
        type: 'warning',
      });
    }
  }

  async openStatusMessagePanel(): Promise<void> {
    try {
      const response = await firstValueFrom(this.statusMessageService.getToastMessagePollingResponse({isRead: false}));
      this.toasts = this.formatMessageResponse(response?.data?.messages);
      this.panelToasts.set(this.toasts.length > this.panelToastsCount ? this.toasts.slice(0, this.panelToastsCount) : this.toasts);
      this.statusMessageService.setToastCount(response?.data?.unreadMessagesCount);
      this.debouncePollingTimer();
      this.showTopToasts.set(this.toasts.filter(toast => toast.notified === false));
      this.showStatusMessages();
    } catch(e) {
      this.snackbarService.show({
        message: this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.LIST_FAILED_MSG) || 'Unable to retrieve service messages. Please try again later.',
        type: 'warning',
      });
    }
  }

  ngAfterViewInit(): void {
    this.matMenuTrigger.menuOpened.subscribe(() => {
      this.alignStatusMessagesPanelToWindowRight();
    });
    this.matMenuTrigger.menuClosed.subscribe(() => {
      this.resetStatusMessagesPanelAlignment();
    });
  }

  ngOnDestroy(): void {
    this.clearAllTimers();
  }

  formatMessageResponse(data: MessageResponse[] = []): Messages[] {
    const toasts: Messages[] = data.map((message: MessageResponse) => {
      return {
        id: message.messageId, // messageId
        status: message.isRead ? 'read' : 'unread', // isRead
        notified: message.isNotified, // isNotified
        publishDateTimeInUTC: message.articlePublishDateTimeInUTC, // articlePublishDateTimeInUTC
        startDateTimeInUTC: convertToLocalTimezone(message.startDate)?.localDateTime, // startDate
        title: message.title, // title
        type: message.messageType, // messageType
        category: message.messageCategory, // category
        shortDescription: message.shortDescription, // shortDescription
        serviceMessageName: message.serviceMessageName,
        linkLabel: message.linkLabel,
        linkCategory: message.linkCategory,
        link: message.link,
        linkAuthField: message.authField,
        linkType: message.linkType,
        iconPath: message.iconPath,
      }
    });
    return toasts;
  }

  debouncePollingTimer(): void {
    clearInterval(this.statusMessagePollingInterval);
    this.startPollingForStatusMessages();
  }

  initializeLabels(): void {
    this.headerService.getHeaderResponseFromObservable().subscribe((resp) => {
      this.statusMessagesLabels = resp.data?.labels;
    });
  }

  navigate(link: string, linkAuthField: string, linkType: string): void {
    this.reusableService.navigateToUrl(new ChildMenu({
      link: link,
      authField: linkAuthField,
      linkType: linkType
    }), this.appContext, link, {useAngularRouter: false});
  }

  showStatusMessages(): void {
    clearTimeout(this.showMessageTimeout);
    this.isToastShown.set(true);
    this.showMessageTimeout = setTimeout(() => {
      this.isToastShown.set(false);
    }, this.duration * 1000);
  }

  markStatusMessageAsNotified(toast: Messages): void {
    const toasts = this.showTopToasts();
    const index = toasts.findIndex(t => t.id === toast.id);
    if(index !== -1) {
      toasts.splice(index, 1);
      this.showTopToasts.set(toasts);
    }
  }

  getTimeForPanel(currentDate: string | Date): string {
    const date = new Date(currentDate);
    if(date.toLocaleDateString() === new Date().toLocaleDateString()) {
      if(date.getHours() === new Date().getHours() && date.getMinutes() === new Date().getMinutes()) {
        return this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.A_FEW_SECONDS_AGO ) || 'A few seconds ago';
      } else if(date.getHours() === new Date().getHours() && date.getMinutes() < new Date().getMinutes()) {
        return `${new Date().getMinutes() - date.getMinutes()} ${this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.MINUTES_AGO) || 'minutes ago'}`;
      }
      return `${this.contentFilterPipe.transform(this.statusMessagesLabels, this.LABELS.TODAY_COMMA) || 'Today,'} ${this.datePipe.transform(date, 'h:mm a')}`;
    }
    return this.datePipe.transform(date, 'MMMM d, h:mm a') || '';
  }

  closeStatusMessagePanel(): void {
    this.matMenuTrigger.closeMenu();
  }

  alignStatusMessagesPanelToWindowRight(): void {
    // Cancel any pending cleanup from a just-closed panel so it doesn't wipe
    // out the alignment styles we're about to apply.
    clearTimeout(this.resetAlignmentTimeout);
    this.resetAlignmentTimeout = null;
    requestAnimationFrame(() => {
      const allBoundingBoxes = Array.from(
        this.document.querySelectorAll<HTMLElement>('.cdk-overlay-connected-position-bounding-box')
      );

      const boundingBox = allBoundingBoxes.find((box) => !!box.querySelector('.status-messages-panel')) || null;
      const overlayPane = boundingBox?.querySelector<HTMLElement>('.cdk-overlay-pane') || null;

      if (!overlayPane || !boundingBox?.classList.contains('cdk-overlay-connected-position-bounding-box')) {
        return;
      }

      boundingBox.classList.add(this.overlayBoundingBoxClass);
      boundingBox.classList.remove(this.overlayAlignedClass);

      const viewportWidth = this.document.documentElement.clientWidth;
      // Keep a stable fixed width matching SCSS to avoid reflow-based jump.
      const panelWidth = 600;
      const alignedLeft = Math.max(viewportWidth - panelWidth, 0);

      boundingBox.style.setProperty('bottom', 'auto', 'important');
      boundingBox.style.setProperty('left', `${alignedLeft}px`, 'important');
      boundingBox.style.setProperty('right', '0px', 'important');
      boundingBox.style.setProperty('width', `${panelWidth}px`, 'important');

      overlayPane.style.setProperty('position', 'absolute', 'important');
      overlayPane.style.setProperty('top', '0px', 'important');
      overlayPane.style.setProperty('bottom', 'auto', 'important');
      overlayPane.style.setProperty('left', '0px', 'important');
      overlayPane.style.setProperty('right', '0px', 'important');
      overlayPane.style.setProperty('width', `${panelWidth}px`, 'important');
      overlayPane.style.setProperty('transform', 'none', 'important');

      requestAnimationFrame(() => {
        boundingBox.classList.add(this.overlayAlignedClass);
      });
    });
  }

  resetStatusMessagesPanelAlignment(): void {
    const boundingBox = this.document.querySelector<HTMLElement>(`.cdk-overlay-connected-position-bounding-box.${this.overlayBoundingBoxClass}`) || null;
    const overlayPane = boundingBox?.querySelector<HTMLElement>('.cdk-overlay-pane') || null;

    if (!boundingBox) {
      return;
    }

    const cleanup = (): void => {
      boundingBox.classList.remove(this.overlayBoundingBoxClass);
      boundingBox.classList.remove(this.overlayAlignedClass);
      boundingBox.removeAttribute('style');
      overlayPane?.removeAttribute('style');
      this.resetAlignmentTimeout = null;
    };

    // Defer the cleanup until the MatMenu close animation has finished.
    // Stripping these inline `!important` styles synchronously on `menuClosed`
    // caused the panel to snap back to CDK's default connected-position anchor
    // (near the trigger button) for a frame while the close animation was
    // still playing, producing a visible left/right jump right before it fully
    // disappeared.
    clearTimeout(this.resetAlignmentTimeout);
    this.resetAlignmentTimeout = setTimeout(cleanup, this.overlayResetDelay);
  }

  redirectToServiceMessagesCenter(): void {
    this.closeStatusMessagePanel();
    this.navigate(this.serviceMessagesRoute, '', '');
  }

}